import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useEmailQuota, useScrapflyUsage, usePortalHealth, useBriefingStats, useSmartAlertRuns } from '../../hooks/useAdmin';
import { formatDate, sortRows, toggleDir } from './adminUtils';

const COLORS = { healthy: '#27ae60', warning: '#e67e22', zero: '#e74c3c', remaining: '#e0e0e0' };

export default function AdminOverview() {
    const navigate = useNavigate();
    const { data: emailQuota, isLoading: emailLoading } = useEmailQuota();
    const { data: scrapfly, isLoading: sfLoading } = useScrapflyUsage();
    const { data: portalHealth, isLoading: phLoading } = usePortalHealth();
    const { data: briefingStats, isLoading: bsLoading } = useBriefingStats();
    const { data: smartAlertRuns = [], isLoading: saLoading } = useSmartAlertRuns();
    const [healthDialog, setHealthDialog] = useState(null); // { label, portals }
    const [healthSort, setHealthSort] = useState({ key: 'articles_today', dir: 'desc' });

    const loading = emailLoading || sfLoading || phLoading || bsLoading || saLoading;

    // Smart Alert stats (derived client-side)
    const saStats = (() => {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayRuns = smartAlertRuns.filter(r => r.timestamp_started && new Date(r.timestamp_started) >= todayStart);
        return {
            runsToday: todayRuns.length,
            errorsToday: todayRuns.filter(r => r.status === 'failed').length,
            sentToday: todayRuns.reduce((sum, r) => sum + (r.articles_sent || 0), 0),
        };
    })();

    // Email chart data
    const emailSent = emailQuota?.sent_today ?? 0;
    const emailRemaining = Math.max((emailQuota?.daily_limit ?? 300) - emailSent, 0);
    const emailData = [
        { name: 'Poslano', value: emailSent },
        { name: 'Preostalo', value: emailRemaining },
    ];

    // Monthly email projection
    const periodSent = emailQuota?.period_sent ?? 0;
    const monthStart = emailQuota?.month_start;
    let monthlyProjection = null;
    if (monthStart) {
        const daysSinceStart = Math.max(Math.ceil((Date.now() - new Date(monthStart).getTime()) / 86400000), 1);
        const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
        monthlyProjection = Math.round((periodSent / daysSinceStart) * daysInMonth);
    }

    // ScrapFly chart data
    const sfPeriod = scrapfly?.period;
    const sfUsed = sfPeriod?.total_requests ?? 0;
    const sfQuota = sfPeriod?.monthly_quota ?? 200000;
    const sfRemaining = Math.max(sfQuota - sfUsed, 0);
    const sfData = [
        { name: 'Potrošeno', value: sfUsed },
        { name: 'Preostalo', value: sfRemaining },
    ];

    // Portal health chart data
    const healthGroups = { healthy: [], warning: [], zero: [] };
    (portalHealth || []).forEach(p => {
        if (p.articles_today >= 10) healthGroups.healthy.push(p);
        else if (p.articles_today > 0) healthGroups.warning.push(p);
        else healthGroups.zero.push(p);
    });
    const healthData = [
        { name: `Zdravi (≥10)`, value: healthGroups.healthy.length, key: 'healthy' },
        { name: 'Upozorenje (1-9)', value: healthGroups.warning.length, key: 'warning' },
        { name: 'Bez članaka (0)', value: healthGroups.zero.length, key: 'zero' },
    ].filter(d => d.value > 0);
    const healthColorMap = { healthy: COLORS.healthy, warning: COLORS.warning, zero: COLORS.zero };

    const handleHealthClick = (entry) => {
        const group = healthGroups[entry.key];
        if (group && group.length > 0) {
            setHealthDialog({ label: entry.name, portals: group });
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 3 }}>
                Admin Dashboard
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                {/* Email Quota Card */}
                <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Email kvota</Typography>
                    <Box sx={{ height: 180 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={emailData} innerRadius={50} outerRadius={75} dataKey="value" startAngle={90} endAngle={-270}>
                                    <Cell fill={emailSent > (emailQuota?.warning_threshold ?? 270) ? COLORS.zero : COLORS.healthy} />
                                    <Cell fill={COLORS.remaining} />
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                        <b>{emailSent}</b> / {emailQuota?.daily_limit ?? 300} danas
                    </Typography>
                    {monthlyProjection != null && (
                        <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 0.5 }}>
                            Mjesečna projekcija: ~{monthlyProjection} email-ova ({periodSent} od {monthStart})
                        </Typography>
                    )}
                    <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                        <Button size="small" onClick={() => navigate('/admin/emails')}>Više detalja</Button>
                    </Box>
                </Paper>

                {/* ScrapFly Quota Card */}
                <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>ScrapFly kvota</Typography>
                    <Box sx={{ height: 180 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={sfData} innerRadius={50} outerRadius={75} dataKey="value" startAngle={90} endAngle={-270}>
                                    <Cell fill={sfUsed > sfQuota * 0.9 ? COLORS.zero : COLORS.healthy} />
                                    <Cell fill={COLORS.remaining} />
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                        <b>{sfUsed.toLocaleString()}</b> / {sfQuota.toLocaleString()} ({sfPeriod?.period_start ?? '—'})
                    </Typography>
                    {sfPeriod && (
                        <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 0.5 }}>
                            Prosjek: {sfPeriod.daily_average}/dan · Projekcija: {sfPeriod.projected_total.toLocaleString()}
                        </Typography>
                    )}
                    <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                        <Button size="small" onClick={() => navigate('/admin/scrapfly')}>Više detalja</Button>
                    </Box>
                </Paper>

                {/* Claude API Card */}
                <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Claude API</Typography>
                    {briefingStats ? (() => {
                        const tokensToday = briefingStats.tokens_today?.total ?? 0;
                        const tokens30d = briefingStats.tokens_30d?.total ?? 0;
                        const inputToday = briefingStats.tokens_today?.input ?? 0;
                        const outputToday = briefingStats.tokens_today?.output ?? 0;
                        const input30d = briefingStats.tokens_30d?.input ?? 0;
                        const output30d = briefingStats.tokens_30d?.output ?? 0;
                        const formatTokens = (n) => {
                            if (!n) return '0';
                            if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
                            if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
                            return n.toString();
                        };
                        const tokenData = [
                            { name: 'Input tokeni', value: inputToday || 0 },
                            { name: 'Output tokeni', value: outputToday || 0 },
                        ];
                        const hasTokens = tokensToday > 0;
                        return (
                            <>
                                <Box sx={{ height: 180 }}>
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie data={hasTokens ? tokenData : [{ name: 'Nema', value: 1 }]} innerRadius={50} outerRadius={75} dataKey="value" startAngle={90} endAngle={-270}>
                                                {hasTokens
                                                    ? <><Cell fill="#5e35b1" /><Cell fill="#ab47bc" /></>
                                                    : <Cell fill={COLORS.remaining} />
                                                }
                                            </Pie>
                                            <Tooltip formatter={(value) => formatTokens(value)} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                                    Danas: <b>{formatTokens(tokensToday)}</b> tokena
                                </Typography>
                                <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 0.5 }}>
                                    In: {formatTokens(inputToday)} · Out: {formatTokens(outputToday)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block' }}>
                                    30d: {formatTokens(tokens30d)} (in: {formatTokens(input30d)} · out: {formatTokens(output30d)})
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                    <Chip label={`${briefingStats.runs_today} run${briefingStats.runs_today !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                                    {briefingStats.errors_today > 0 && (
                                        <Chip label={`${briefingStats.errors_today} error`} size="small" color="error" variant="outlined" />
                                    )}
                                    {(briefingStats.disambiguated_today ?? 0) > 0 && (
                                        <Chip label={`${briefingStats.disambiguated_today} disambig`} size="small" color="info" variant="outlined" />
                                    )}
                                </Box>
                            </>
                        );
                    })() : (
                        <Typography variant="body2" color="text.secondary">Nema podataka</Typography>
                    )}
                    <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                        <Button size="small" onClick={() => navigate('/admin/briefings')}>Više detalja</Button>
                    </Box>
                </Paper>

                {/* Voyage API (Embeddings) Card */}
                <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Voyage API</Typography>
                    {briefingStats ? (() => {
                        const embToday = briefingStats.embeddings_today ?? 0;
                        const emb30d = briefingStats.embeddings_30d ?? 0;
                        const embData = [
                            { name: 'Danas', value: embToday },
                            { name: '30d (ostalo)', value: Math.max(emb30d - embToday, 0) },
                        ];
                        const hasEmb = emb30d > 0;
                        return (
                            <>
                                <Box sx={{ height: 180 }}>
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie data={hasEmb ? embData : [{ name: 'Nema', value: 1 }]} innerRadius={50} outerRadius={75} dataKey="value" startAngle={90} endAngle={-270}>
                                                {hasEmb
                                                    ? <><Cell fill="#00897b" /><Cell fill="#b2dfdb" /></>
                                                    : <Cell fill={COLORS.remaining} />
                                                }
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                                    Danas: <b>{embToday.toLocaleString()}</b> embedding{embToday !== 1 ? 's' : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 0.5 }}>
                                    30d ukupno: {emb30d.toLocaleString()} embeddings
                                </Typography>
                                <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block' }}>
                                    Aktivni briefingsi: {briefingStats.active_briefings} / {briefingStats.total_briefings}
                                </Typography>
                            </>
                        );
                    })() : (
                        <Typography variant="body2" color="text.secondary">Nema podataka</Typography>
                    )}
                </Paper>

                {/* Portal Health Card */}
                <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Zdravlje portala</Typography>
                    <Box sx={{ height: 180 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={healthData}
                                    innerRadius={50}
                                    outerRadius={75}
                                    dataKey="value"
                                    onClick={handleHealthClick}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {healthData.map((entry) => (
                                        <Cell key={entry.key} fill={healthColorMap[entry.key]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                        {healthData.map(d => (
                            <Typography key={d.key} variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: healthColorMap[d.key] }} />
                                {d.name}: {d.value}
                            </Typography>
                        ))}
                    </Box>
                </Paper>

                {/* Smart Alerti Card */}
                <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Smart Alerti</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 2 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                            {saStats.runsToday}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">pokretanja danas</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Chip
                                label={`${saStats.sentToday} poslano`}
                                size="small"
                                variant="outlined"
                                sx={{ color: '#27ae60', borderColor: '#27ae60' }}
                            />
                            {saStats.errorsToday > 0 && (
                                <Chip
                                    label={`${saStats.errorsToday} greška`}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Button size="small" onClick={() => navigate('/admin/smart-alerts')}>Više detalja</Button>
                    </Box>
                </Paper>
            </Box>

            {/* Portal Health Dialog */}
            <Dialog open={!!healthDialog} onClose={() => setHealthDialog(null)} maxWidth="md" fullWidth>
                <DialogTitle>{healthDialog?.label}</DialogTitle>
                <DialogContent sx={{ px: 1 }}>
                    {(() => {
                        const portals = healthDialog?.portals || [];
                        const sorted = sortRows(portals, healthSort.key, healthSort.dir, (r, k) => {
                            if (k === 'portal_name') return r.portal_name;
                            if (k === 'articles_today') return r.articles_today;
                            if (k === 'last_scraped') return r.last_scraped ?? '';
                            return '';
                        });
                        const hsp = (key) => ({
                            active: healthSort.key === key,
                            direction: healthSort.key === key ? healthSort.dir : 'asc',
                            onClick: () => setHealthSort(prev => toggleDir(prev, key)),
                        });
                        return (
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 600 }}><TableSortLabel {...hsp('portal_name')}>Portal</TableSortLabel></TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right"><TableSortLabel {...hsp('articles_today')}>Članci danas</TableSortLabel></TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}><TableSortLabel {...hsp('last_scraped')}>Zadnje scrapanje</TableSortLabel></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sorted.map(p => (
                                        <TableRow key={p.source_name} sx={p.articles_today === 0 ? { backgroundColor: '#fff5f5' } : {}}>
                                            <TableCell sx={{ fontSize: 13 }}>{p.portal_name}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }} align="right">{p.articles_today}</TableCell>
                                            <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{formatDate(p.last_scraped)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
